import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { computeCopyMode, computeMoveMode } from '../../permissions/modes';
import { useFileManagerPermissions } from '../../permissions/useFileManagerPermissions';
import { useFileManagerMounts } from '../core/useFileManagerMounts';
import { evaluateDrop } from './dragGuards';
import { useFileManagerDrag } from './useFileManagerDrag';
import { useFileManagerTransfer } from '../transfer/useFileManagerTransfer';
import type { TransferPolicy } from '../transfer/transferPolicy';
import type { FileSystemItem } from '../../types';

export const useDropTarget = (
  path: MaybeRefOrGetter<string>,
  item?: MaybeRefOrGetter<FileSystemItem | undefined>,
) => {
  const { draggedItems, dragOverPath } = useFileManagerDrag();
  const { requestTransfer } = useFileManagerTransfer();
  const { mountAt } = useFileManagerMounts();
  const { permissionsAt } = useFileManagerPermissions();

  const isDragOver = computed(() => dragOverPath.value === toValue(path));

  /**
   * The gate is chosen AFTER evaluating, because which permission applies depends on which verb the
   * drop resolves to — which in turn depends on where it lands. This is why dragGuards knows nothing
   * about permissions: it answers "what would this drop mean", not "may you".
   *
   * Two-sided, now that capabilities are per mount: the verb has to be permitted where the items come
   * FROM (you are removing them from there, for a move) and where they are going TO (you are writing
   * them in). Asking only the source would let a project you may edit push content into one you may
   * only read. Within a single project both sides carry the same capability set, so this is the same
   * single check it always was there.
   */
  const isPermitted = (policy: TransferPolicy) => {
    const gate = policy.operation === 'copy' ? computeCopyMode : computeMoveMode;
    // evaluateDrop has already asserted the whole selection shares one source mount, so items[0]
    // speaks for all of them.
    const sourcePath = draggedItems.value[0]?.path;
    if (sourcePath === undefined) return false;
    return (
      gate(permissionsAt(sourcePath)) === 'enabled' &&
      gate(permissionsAt(toValue(path))) === 'enabled'
    );
  };

  const verdict = computed(() => evaluateDrop(toValue(path), draggedItems.value, mountAt));

  /**
   * What the browser should show on the cursor over this zone: the OS-native "+" badge for a copy,
   * the plain move cursor otherwise, and 'none' where the drop cannot happen at all. A computed, so
   * it is recalculated when the dragged selection changes rather than on every dragover tick.
   *
   * This is the ONLY affordance distinguishing the two verbs mid-drag — deliberately no second
   * highlight style, since a copy that discloses gets a full modal and one that does not is
   * unremarkable.
   *
   * `null` means "this is not an internal item drag — do not touch dropEffect at all". Load-bearing:
   * an OS file drag (upload) populates no draggedItems, so every verdict would come back 'invalid',
   * and writing 'none' would tell the browser to refuse a drop that DropZone's upload half is about
   * to accept.
   */
  const dropEffect = computed<'copy' | 'move' | 'none' | null>(() => {
    if (draggedItems.value.length === 0) return null;
    const current = verdict.value;
    if (current.kind !== 'ok' || !isPermitted(current.policy)) return 'none';
    return current.policy.operation;
  });

  const onDragEnter = () => {
    const current = verdict.value;
    if (current.kind === 'ok' && isPermitted(current.policy)) {
      dragOverPath.value = toValue(path);
    }
  };

  const onDragLeave = (event: DragEvent) => {
    if (dragOverPath.value !== toValue(path)) return;
    const related = event.relatedTarget as Node | null;
    const current = event.currentTarget as HTMLElement | null;
    if (related && current?.contains(related)) return;
    dragOverPath.value = null;
  };

  /**
   * A refused drop is now silent. The old cross-library toast existed because the two libraries were
   * indistinguishable in the UI, so ignoring the drop read as a bug — but crossing that boundary is
   * exactly what this now supports. What is left ('no-destination') is a drop onto the virtual root
   * or an unresolvable path: visibly not a folder, and already showing a 'none' cursor.
   */
  const onDrop = () => {
    const targetPath = toValue(path);
    const current = verdict.value;
    const destinationMount = mountAt(targetPath);

    if (
      current.kind === 'ok' &&
      isPermitted(current.policy) &&
      isDragOver.value &&
      destinationMount
    ) {
      requestTransfer({
        // Snapshotted, not referenced: the drag ends the moment the pointer is released, and
        // onDragEnd clears draggedItems — while a confirmation modal may hold this intent for as
        // long as the user takes to read it.
        items: [...draggedItems.value],
        destinationParentPath: targetPath,
        destinationItem: toValue(item),
        destinationMount,
        policy: current.policy,
      });
    }
    dragOverPath.value = null;
  };

  return { isDragOver, dropEffect, onDragEnter, onDragLeave, onDrop };
};
